import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { get, isEqual } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import styled, { useTheme } from 'styled-components';

import { FavoritesCardList } from 'components/favorites-card-list';
import { LoadingAnimation } from 'components/loading-animation';
import { LoadingContainer } from 'components/loading-animation/loading-container';
import { MenuTileGrid } from 'components/menu-tile-grid';
import { MenuTileStack } from 'components/menu-tile-stack';
import { MenuTileUnavailable } from 'components/menu-tile-unavailable';
import { RecentOrderCardList } from 'components/recent-order-card-list';
import { StoreSelected } from 'components/store-selected';
import { DesktopTabbedHeader, TabbedHeader } from 'components/tabbed-header';
import { MenuObjectTypes } from 'enums/menu';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { useMenuByDayPart } from 'hooks/use-menu-by-day-part';
import { useTabbedMenuHeader } from 'hooks/use-tabbed-menu-header';
import { SHOULD_CHECK_ITEM_IN_OFFER } from 'pages/main-menu/constants';
import { ProductWizard } from 'pages/product-wizard';
import { useCdpContext } from 'state/cdp';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/main-menu';
import { useMenuContext } from 'state/menu';
import { SectionUiPattern } from 'state/menu/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { logger } from 'utils/logger';
import { getType } from 'utils/menu';
import { isFavoritesRoute, isRecentItemsRoute, routes } from 'utils/routing';

import { filterWithChannelExclusions } from '../main-menu/utils';

import { ItemUnavailable } from './item-unavailable';
import { MainContainer } from './main-container';
import { MainMenuHeader } from './main-menu-header';
import { MainMenuLink } from './main-menu-link';

const InnerContentContainer = styled.div`
  background-color: ${({ $enableDirectAccessToMenu }) =>
    $enableDirectAccessToMenu ? Styles.color.background : 'transparent'};

  & > :last-child {
    padding-block-end: ${({ $enableDirectAccessToMenu }) =>
      $enableDirectAccessToMenu ? 0 : primitive.$spacing3};
  }
`;

const RenderStacked = memo(({ sections }) =>
  sections.map(section => (
    <MenuTileStack
      key={section._id}
      options={section.options}
      name={section.name?.locale}
      image={section.image}
      description={section.description}
    />
  ))
);

const RenderInlineTileGrid = memo(({ sections }) => {
  return sections.map(section => (
    <MenuTileGrid
      key={section._id}
      data={section.options || []}
      gridTitle={get(section, 'name.locale')}
      objectFitContain={true}
    />
  ));
});

const RenderStandard = memo(({ sections }) =>
  sections.length > 0 ? <MenuTileGrid data={sections} objectFitContain /> : null
);

const isPickerItemOrComboOnMenuObject = menuObject =>
  menuObject && ['picker', 'item', 'combo'].some(type => type === getType(menuObject.data));

export const InnerContent = ({
  menuObject,
  shouldUseTabbedMenuBar,
  path,
  isOffer,
  isStaticMenu,
  serviceMode,
  checkAvailability = true,
  menuOptions,
}) => {
  const enableDirectAccessToMenu = useFlag(LaunchDarklyFlag.ENABLE_DIRECT_ACCESS_TO_MENU);

  const filteredOptions = (menuOptions ?? [])?.filter(option => {
    // Filter menu options based on channel exclusions
    const filteredWithChannelExclusions = filterWithChannelExclusions(
      option.channelExclusions,
      serviceMode
    );
    // Filter menu options based on static menu option
    const filteredWithStaticMenu = isStaticMenu ? option.showInStaticMenu : true;
    return filteredWithChannelExclusions && filteredWithStaticMenu;
  });

  const stackedSections = useMemo(() => {
    if (!Array.isArray(filteredOptions)) {
      return [];
    }
    return filteredOptions.filter(
      option => option && option.uiPattern === SectionUiPattern.STACKED
    );
  }, [filteredOptions]);

  const inlineTileSections = useMemo(() => {
    if (!Array.isArray(filteredOptions)) {
      return [];
    }
    return filteredOptions.filter(
      option => option && option.uiPattern === SectionUiPattern.INLINE_TILE
    );
  }, [filteredOptions]);

  const standardSections = useMemo(() => {
    if (!Array.isArray(filteredOptions)) {
      return [];
    }
    return filteredOptions.filter(
      option =>
        option &&
        option.uiPattern !== SectionUiPattern.STACKED &&
        option.uiPattern !== SectionUiPattern.INLINE_TILE
    );
  }, [filteredOptions]);

  // This inner content is reused on the main menu too and this check is not needed there
  if (checkAvailability && !menuObject.data) {
    if (isRecentItemsRoute(path)) {
      return <RecentOrderCardList />;
    }
    if (isFavoritesRoute(path)) {
      return <FavoritesCardList />;
    }
    return <ItemUnavailable isOffer={isOffer} />;
  }

  if (isPickerItemOrComboOnMenuObject(menuObject)) {
    // will eventually decouple from menu content and use layout to route to its page
    return <ProductWizard product={menuObject.data} />;
  }

  if (!filteredOptions?.length) {
    return <MenuTileUnavailable />;
  }

  return (
    <InnerContentContainer $enableDirectAccessToMenu={enableDirectAccessToMenu}>
      {!enableDirectAccessToMenu && (
        <MainMenuLink backToFullMenu={shouldUseTabbedMenuBar} type={getType(menuObject.data)} />
      )}

      <RenderStandard sections={standardSections} />
      <RenderStacked sections={stackedSections} />
      <RenderInlineTileGrid sections={inlineTileSections} />
    </InnerContentContainer>
  );
};

function curriedTryGetMenuObject(getMenuObject, setMenuItemNotFound) {
  return id => {
    try {
      return getMenuObject(id);
    } catch (err) {
      if (err.notFoundError) {
        setMenuItemNotFound(true);

        logger.warn({ error: err, menuObjectId: id });

        return {};
      }

      // not sure we should throw here...
      throw err;
    }
  };
}

const useHandleMenuItemInOffer = setIsOffer => {
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const offerIdParam = useGetUrlSearchParam('offer');
  const navigate = useNavigate();

  return menuItem => {
    const isOfferBenefit = Boolean(offerIdParam);
    setIsOffer(isOfferBenefit);

    const isSelectedOfferBenefit = () => {
      const menuItemId = menuItem._id;

      // If the selected offer in context does not match the one in the URL go back to offers
      if (selectedLoyaltyOffer.loyaltyEngineId !== offerIdParam) {
        return false;
      }

      // If incentive has a discount incentive, we check the discounted product to make sure it applies
      if (selectedLoyaltyOffer?.incentives?.[0]._type === MenuObjectTypes.OFFER_DISCOUNT) {
        return selectedLoyaltyOffer?.incentives[0].discountProduct?._id === menuItemId;
      }

      return selectedLoyaltyOffer?.incentives?.some(incentive => incentive._id === menuItemId);
    };

    // This will prevent the case of a user going back to the product wizard for an offer item
    // without having the offer context causing price to show 0 and the item not adding to cart
    if (
      isOfferBenefit &&
      (!selectedLoyaltyOffer || (selectedLoyaltyOffer && !isSelectedOfferBenefit()))
    ) {
      navigate(routes.offers);
    }
  };
};

const NewMenuContent = () => {
  const { formatMessage } = useIntl();
  const { id } = useParams();
  const { region } = useLocale();
  const {
    loading,
    data: menuData,
    getMainMenu,
    getMenuObject,
    isOffer,
    setIsOffer,
    isStaticMenu,
  } = useMainMenuContext();
  const navigate = useNavigate();
  const location = useLocation();

  // TODO ICFE-429 - useMediaQuery is causing masive rerenderings and it should be replaced by context in order to calculate the device type once per page.
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const theme = useTheme();
  const { menuId, featureMenuLoading } = useFeatureMenu();
  const { store } = useStoreContext();
  const storeId = store.number || '';
  const handleMenuItemInOffer = useHandleMenuItemInOffer(setIsOffer);
  const shouldUseTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);

  const [menuItemNotFound, setMenuItemNotFound] = useState(false);
  // Trying to leave this intact causes cascading re-renders
  const memoizedMenu = useMemo(() => getMainMenu(), [getMainMenu]);
  const mainMenu = menuData?.Menu || memoizedMenu;
  const { logPageView, logCommercePageView } = useCdpContext();
  const loggedView = useRef({});

  const isLoading = featureMenuLoading || loading;

  useEffect(() => {
    async function mainMenu() {
      if (!menuData && !loading) {
        await getMainMenu(menuId, region, storeId);
      }
    }

    mainMenu();
  }, [getMainMenu, loading, menuData, menuId, region, storeId]);

  const {
    activeTab,
    setAsActiveTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const tryGetMenuObject = useMemo(
    () => curriedTryGetMenuObject(getMenuObject, setMenuItemNotFound),
    [getMenuObject]
  );

  // if its recent items or favorites don't get menu data
  const getMenuObjectData =
    isRecentItemsRoute(location.pathname) || isFavoritesRoute(location.pathname)
      ? { loading: false, data: null }
      : tryGetMenuObject(id);

  const menuObject = useMenuByDayPart(getMenuObjectData);
  const documentTitle = get(menuObject, 'data.name.locale', '');

  useEffect(() => {
    if (!isLoading && !isEqual(loggedView.current, menuObject.data) && !menuItemNotFound) {
      if (!menuObject.data) {
        return;
      }

      const { data: menuObjectData } = menuObject;

      if (SHOULD_CHECK_ITEM_IN_OFFER) {
        handleMenuItemInOffer(menuObjectData);
      }

      const sections = {
        name: menuObjectData.name?.locale,
        menuType: menuObjectData?._type,
        id: menuObjectData?._id,
      };

      // mParticle page view event - uses mParticle logPageView API to track page view
      logPageView(
        location.pathname.replace(/(menu)\/(.*)/, `$1/${getType(menuObject.data)}`),
        {
          ...sections,
          pathname: location.pathname,
        },
        {},
        true
      );

      // mParticle eCommerce product view event
      const isMenuPickerOrItemPage = /^\/(menu)\/([item|picker].*)$/.test(location.pathname);
      if (isMenuPickerOrItemPage) {
        logCommercePageView(sections, { path: location.pathname });
      }

      loggedView.current = menuObject.data;
    }
  }, [
    logPageView,
    menuItemNotFound,
    menuObject.data,
    menuObject.loading,
    location,
    menuObject,
    logCommercePageView,
    loading,
    menuData,
    isLoading,
    handleMenuItemInOffer,
  ]);

  if (menuItemNotFound) {
    navigate(`${routes.notFound}/404`, { replace: true });
  }

  const shouldRenderTabbedHeader =
    shouldUseTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile;

  if (isLoading) {
    return (
      <LoadingContainer
        ref={node => {
          node?.focus();
        }}
        tabIndex={0}
        aria-label={formatMessage({ id: 'loading' })}
      >
        <LoadingAnimation fillColor={theme.token('icon-active')} />
      </LoadingContainer>
    );
  }

  const isItemAvailable = !isLoading && menuObject.data?._id;

  return (
    <StoreSelected>
      <Helmet title={documentTitle} />

      {isItemAvailable && <MainMenuHeader />}

      <MainContainer data-testid="menu-content" $addMainMenuHeaderHeight={!isItemAvailable}>
        {shouldRenderTabbedHeader && (
          <TabbedHeader
            data={tabbedHeaderItems}
            loading={isLoading || isTabbedMenuLoading}
            activeTab={activeTab}
            setAsActiveTab={setAsActiveTab}
          />
        )}
        <InnerContent
          menuOptions={menuObject.data?.options}
          menuObject={menuObject}
          pickerBackgroundImage={get(mainMenu, 'pickerBackgroundImage')}
          path={location.pathname}
          shouldUseTabbedMenuBar={shouldUseTabbedMenuBar}
          isOffer={isOffer}
          isStaticMenu={isStaticMenu}
        />
      </MainContainer>
    </StoreSelected>
  );
};

const LegacyMenuContent = () => {
  const { formatMessage } = useIntl();
  const { getMenuObject, getMainMenu, isOffer, setIsOffer, isStaticMenu } = useMenuContext();

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const { serviceMode } = useServiceModeContext();
  const theme = useTheme();
  const handleMenuItemInOffer = useHandleMenuItemInOffer(setIsOffer);
  const shouldUseTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);
  const enableDirectAccessToMenu = useFlag(LaunchDarklyFlag.ENABLE_DIRECT_ACCESS_TO_MENU);

  const [menuItemNotFound, setMenuItemNotFound] = useState(false);
  const tryGetMenuObject = useMemo(
    () => curriedTryGetMenuObject(getMenuObject, setMenuItemNotFound),
    [getMenuObject]
  );

  // if its recent items or favorites don't get menu data
  const menuObjectData =
    isRecentItemsRoute(location.pathname) || isFavoritesRoute(location.pathname)
      ? { loading: false, data: null }
      : tryGetMenuObject(id);

  const menuObject = useMenuByDayPart(menuObjectData);
  const mainMenu = useMemo(() => getMainMenu(), [getMainMenu]);
  const { logPageView, logCommercePageView } = useCdpContext();
  const loggedView = useRef({});
  const documentTitle = get(menuObject, 'data.name.locale', '');

  useEffect(() => {
    if (!menuObject.loading && !isEqual(loggedView.current, menuObject.data) && !menuItemNotFound) {
      const { data: menuData } = menuObject;

      if (!menuData) {
        return;
      }

      if (SHOULD_CHECK_ITEM_IN_OFFER) {
        handleMenuItemInOffer(menuData);
      }

      const sections = {
        name: menuData.name?.locale,
        menuType: menuData?._type,
        id: menuData?._id,
      };

      // mParticle page view event - uses mParticle logPageView API to track page view
      logPageView(
        location.pathname.replace(/(menu)\/(.*)/, `$1/${getType(menuObject.data)}`),
        {
          ...sections,
          pathname: location.pathname,
        },
        {},
        true
      );

      // mParticle eCommerce product view event
      const isMenuPickerOrItemPage = /^\/(menu)\/([item|picker].*)$/.test(location.pathname);
      if (isMenuPickerOrItemPage) {
        logCommercePageView(sections, { path: location.pathname });
      }

      loggedView.current = menuObject.data;
    }
  }, [
    logPageView,
    menuItemNotFound,
    menuObject.data,
    menuObject.loading,
    location,
    menuObject,
    logCommercePageView,
    handleMenuItemInOffer,
  ]);

  if (menuItemNotFound) {
    navigate(`${routes.notFound}/404`, { replace: true });
  }

  const {
    activeTab,
    setAsActiveTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const shouldRenderTabbedHeader =
    shouldUseTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile;

  const isItemAvailable = !menuObject.loading && menuObject.data;

  const shouldRenderMainMenuHeader = isItemAvailable && !enableDirectAccessToMenu;

  const shouldRenderDirectAccessTabbedHeader =
    isItemAvailable && enableDirectAccessToMenu && !isMobile;

  return (
    <StoreSelected>
      <Helmet title={documentTitle} />

      {shouldRenderMainMenuHeader && <MainMenuHeader />}

      {shouldRenderDirectAccessTabbedHeader && (
        <DesktopTabbedHeader
          items={tabbedHeaderItems}
          activeTabIndex={activeTab}
          isLoading={mainMenu.loading || isTabbedMenuLoading}
        />
      )}

      <MainContainer data-testid="menu-content">
        {shouldRenderTabbedHeader && (
          <TabbedHeader
            data={tabbedHeaderItems}
            loading={mainMenu.loading || isTabbedMenuLoading}
            activeTab={activeTab}
            setAsActiveTab={setAsActiveTab}
          />
        )}

        {menuObject.loading ? (
          <LoadingContainer
            ref={node => {
              node?.focus();
            }}
            tabIndex={0}
            aria-label={formatMessage({ id: 'loading' })}
          >
            <LoadingAnimation fillColor={theme.token('icon-active')} />
          </LoadingContainer>
        ) : (
          <InnerContent
            menuOptions={menuObject.data?.options}
            menuObject={menuObject}
            pickerBackgroundImage={get(mainMenu.data, 'pickerBackgroundImage')}
            path={location.pathname}
            shouldUseTabbedMenuBar={shouldUseTabbedMenuBar}
            isOffer={isOffer}
            isStaticMenu={isStaticMenu}
            serviceMode={serviceMode}
          />
        )}
      </MainContainer>
    </StoreSelected>
  );
};

export const MenuContent = () => {
  const enableDirectMenuAccess = useFlag(LaunchDarklyFlag.ENABLE_DIRECT_ACCESS_TO_MENU);
  const location = useLocation();

  if (enableDirectMenuAccess && /section-/.test(location.pathname)) {
    // this is needed so the old links that point to a section work when the direct access to menu is enabled
    const redirectURL = location.pathname.replace('/menu/', '/menu#');
    return <Navigate to={redirectURL} />;
  }

  if (/section-/.test(location.pathname)) {
    return <NewMenuContent />;
  }

  return <LegacyMenuContent />;
};
